import React, { useEffect, useRef, useState } from 'react';
import FormFieldContext from 'components/forms/FormFieldContext';
import SecondaryButton from 'components/buttons/SecondaryButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import { BillingData, createPaymentMethod, PaymentMethod, PaymentMethodType } from '../BillingTypes';
import { useModal } from 'components/modals/ModalContext';
import { useGlobalContext } from 'GlobalContext';
import { CardElement, IbanElement, useElements, useStripe } from '@stripe/react-stripe-js';
import Dropdown from 'components/forms/basefields/Dropdown';
import { useTranslation } from 'react-i18next';
import { BillingDetailsRef } from '../subscription-wizard/BillingDetails';
import Checkbox from 'components/forms/basefields/Checkbox';
import { createSepaSetupIntent } from '../functions/createSepaSetupIntent';
import { createStripeCustomer } from '../functions/createStripeCustomer';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { encryptData } from 'services/authentication/encryptData';
import '../../../../style/scss/billing.scss';

interface PaymentMethodFormProps {
    billingData: BillingData
}

const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({ billingData }) => {
    const { t } = useTranslation();
    const authContext = useAuthContext(); 
    const { setFloatingAlert } = useGlobalContext();
    const { revealModal, closeModal, modalStack } = useModal();
    const [editing, setEditing] = useState(true);
    const isCompanyNameMissing = !billingData.company_name;
    const isEmailMissing = !billingData.email;
    const [updatedData, setUpdatedData] = useState<Record<string, any>>({});
    const [localBillingData, setLocalBillingData] = useState({
        company_name: billingData.company_name || '',
        email: billingData.email || '',
    });
    const [paymentMethodData, setPaymentMethodData] = useState<PaymentMethod>(createPaymentMethod);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>(paymentMethodData['payment_type'] || 'sepa_debit');
    const [isIbanComplete, setIsIbanComplete] = useState(false);
    const [showAccountHolderWarning, setShowAccountHolderError] = useState(false);
    const [showIbanWarning, setShowIbanWarning] = useState(false);
    const [sepaMandateAccepted, setSepaMandateAccepted] = useState(false);
    const [showWarning, setShowWarning] = useState(false);
    const [buttonLoader, setButtonLoader] = useState(false);
    const billingDetailsRef = useRef<BillingDetailsRef>({ getCardElement: () => null, getIbanElement: () => null });
    const topModal = modalStack[modalStack.length - 1];
    const stripe = useStripe();
    const elements = useElements();

    const [paymentMethods] = useState<{ value: string, name: string }[]>([ 
        { value: 'sepa_debit', name: 'billing.payment_method.sepa' }, 
    ]);
    
    // Reveal the modal on first render
    useEffect(() => {
        revealModal();
    }, []);

    useEffect(() => {
        if (elements) {
            billingDetailsRef.current = {
                getCardElement: () => elements.getElement(CardElement),
                getIbanElement: () => elements.getElement(IbanElement),
            };
        }
    }, [elements]);

    // Handle the submit of the updated billing details
    const handleSubmit = async () => {

        // Show error if sepa mandate is not accepted
        if (!sepaMandateAccepted) {
            setShowWarning(true);

            setFloatingAlert({
                type: "danger",
                message: "billing.general.accept_mandate_required"
            });
            return;
        }

        // Show error if iban is not filled in
        if (selectedPaymentMethod === "sepa_debit" && !isIbanComplete) {
            setShowIbanWarning(true);

            setFloatingAlert({
                type: "danger",
                message: "billing.general.add_valid_iban"
            });
            return;
        }

        // Show error if account holder is nog filled in
        if (!paymentMethodData.sepa_account_holder) {
            setFloatingAlert({
                type: "danger",
                message: "billing.general.add_valid_account_holder"
            });
            return;
        }

        // Get the company name and email
        const finalCompanyName = billingData.company_name || localBillingData.company_name;
        const finalEmail = billingData.email || localBillingData.email;

        // Show error if company name or email are not given
        if (!finalCompanyName || !finalEmail) {
            setFloatingAlert({
                type: "danger",
                message: "billing.general.missing_company_or_email"
            });
            return;
        }

        try {            
            // Start the button loader
            setButtonLoader(true);

            // Combine the billing data
            const enrichedBillingData = {
                ...billingData,
                company_name: finalCompanyName,
                email: finalEmail,
            };

            // Get the current stripe customer id from the billing data
            let stripeCustomerId = billingData.stripe_customer_id;

            // Create the stripe customer when the company details have none yet
            if (!stripeCustomerId) {
                stripeCustomerId = await createStripeCustomer(authContext.environmentHash, enrichedBillingData);
            }

            // Show error if the stripe customer could not be created
            if (!stripeCustomerId) {
                setFloatingAlert({ type: 'danger', message: 'billing.general.payment_method_save_failed' });
                return;
            }

            // Create the stripe sepa setup intent with sepa mandate
            const setupIntent = await createSepaSetupIntent(
                stripe,
                elements,
                stripeCustomerId,
                authContext.environmentHash,
                enrichedBillingData,
                paymentMethodData,
                billingDetailsRef
            );

            // Show error if the payment method is not created
            if (!setupIntent) {
                setFloatingAlert({ type: 'danger', message: 'billing.general.payment_method_save_failed' });
                return;
            }

            // Show success alert
            setFloatingAlert({ 'type': 'success' })

            // Update the authentication context to hide the no payment method banner
            authContext.setSubscriptionNoSepaMandate(false);
            localStorage.setItem("no_payment_method", encryptData(String(false)));

            // Call the on success callback
            if (topModal?.props?.onSuccess) {
                topModal.props.onSuccess();
            };

            // Close the modal
            closeModal();
        } catch (error) {
            console.error("Error on submit", error);
            setFloatingAlert({ type: 'danger', message: 'billing.general.payment_method_save_failed' });
        } finally {
            // Stop the button loader on every path
            setButtonLoader(false);
        };        
    };

    // Close the modal
    const handleClose = () => {
        setButtonLoader(false);
        closeModal();
    }

    return (
        <FormFieldContext.Provider value={{ editing, setEditing, updatedData, setUpdatedData }}>
            <form className='formset add-edit-form payment-method-form'>

                {(isCompanyNameMissing || isEmailMissing) && (
                    <div className="billing-extra-fields">
                        {isCompanyNameMissing && (
                            <div className='formfield'>
                                <label>{t('billing.general.company_name_label')}</label>
                                <input
                                    type="text"
                                    value={localBillingData.company_name}
                                    onChange={(e) => {
                                        setLocalBillingData({
                                            ...localBillingData,
                                            company_name: e.target.value
                                        });
                                    }}
                                    placeholder={t('billing.general.company_name_label')}
                                />
                            </div>
                        )}

                        {isEmailMissing && (
                            <div className='formfield'>
                                <label>{t('billing.general.email_label')}</label>
                                <input
                                    type="email"
                                    value={localBillingData.email}
                                    onChange={(e) => {
                                        setLocalBillingData({
                                            ...localBillingData,
                                            email: e.target.value
                                        });
                                    }}
                                    placeholder={t('billing.general.email_placeholder')}
                                />
                            </div>
                        )}
                    </div>
                )}

                <div className='formfield'>
                    <label>{t('billing.general.payment_method_header')}</label>
                    <Dropdown<{ value: string, name: string }>
                        options={paymentMethods}
                        name='payment_method'
                        disabled_selected={t('billing.general.select_payment_method_disabled_selected')}
                        selectedOption={paymentMethods.find(method => method.value === selectedPaymentMethod)}
                        onChange={(selectedValue) => {
                            setSelectedPaymentMethod(selectedValue);
                            setPaymentMethodData({...paymentMethodData, payment_type: selectedValue as PaymentMethodType})
                        }}
                        selectionFormat={(option) => `${option.name}`}
                        optionFormat='name'
                        allowNoneOption={false} 
                        showSearch={false}
                    />
                </div>

                {selectedPaymentMethod === 'sepa_debit' && (
                    <div>
                        <div className='formfield'>
                            <label>{t('billing.general.iban_holder_name_label')}</label>
                            <input
                                type="text"
                                onChange={(e) => {
                                    setPaymentMethodData({...paymentMethodData, ['sepa_account_holder']: e.target.value});
                                    setShowAccountHolderError(false);
                                }}
                                placeholder={t('billing.general.iban_holder_name_label')}
                                className={`text ${showAccountHolderWarning ? 'invalid' : ''}`}
                            />
                        </div>
                        <div className='formfield'>
                            <label>{t('billing.general.iban_number_label')}</label>
                            <div className={`sepa-field ${showIbanWarning ? 'warning' : ''}`}>
                                <IbanElement
                                    options={{
                                        supportedCountries: ['SEPA'],
                                        style: {
                                            base: {
                                                fontSize: '1rem',
                                                fontFamily: 'Gotham-Book',
                                                color: '#1a1d27',
                                                '::placeholder': {
                                                    color: '#757575',
                                                },
                                            },
                                        },
                                    }}
                                    onChange={(event) => {
                                        setIsIbanComplete(event.complete);
                                        setShowIbanWarning(false);
                                    }}
                                />
                            </div>
                        </div>
                        <br />
                        <div className='mandate'>
                            <h6>
                                {t("billing.general.sepa_mandate_title")}
                            </h6>
                            <p>
                                {t("billing.general.sepa_mandate_description")}
                            </p>
                            <div className={showWarning ? 'warning' : ''}>
                                <Checkbox
                                    name="sepa_mandate_accepted"
                                    label="billing.general.accept_mandate_label"
                                    align='left'
                                    value={sepaMandateAccepted}
                                    onChange={(e) => {
                                        if (e.target.checked === true) {
                                            setShowWarning(false);
                                        }
                                        setSepaMandateAccepted(e.target.checked)
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}
                <div className='buttons-right'>
                    <SecondaryButton
                        onClick={handleClose} 
                        label='general.cancel'
                        size="small"
                    />
                    <PrimaryButton
                        type="button" 
                        label='general.save'
                        onClick={handleSubmit}
                        size="small"
                        onlyViewRestriction={true}
                        loading={buttonLoader}
                    />
                </div>
            </form>
        </FormFieldContext.Provider>
    )
};

export default PaymentMethodForm;